import React from 'react'
import { PageHeader, Card } from '@pilot-hub/ui'
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'

// Custom rotated marker component
const RotatedMarker = ({ position, heading, children }: { position: [number, number]; heading: number; children: React.ReactNode }) => {
  const map = useMap()
  
  React.useEffect(() => {
    if (!map) return
    
    const icon = L.divIcon({
      className: 'custom-drone-marker',
      html: `<div style="transform: rotate(${heading}deg);">${children}</div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    })
    
    const marker = L.marker(position, { icon }).addTo(map)
    
    return () => {
      marker.remove()
    }
  }, [map, position, heading, children])
  
  return null
}

export function TacticalMapPage() {
  const drones = [
    { id: 1, name: 'DJI Matrice 30T', lat: 52.5200, lng: 13.4050, heading: 45 },
    { id: 2, name: 'Mavic 3E', lat: 52.5250, lng: 13.4100, heading: 180 },
    { id: 3, name: 'Mavic 3E', lat: 52.5180, lng: 13.3980, heading: 270 },
  ]

  const airspaceZones = [
    { id: 1, name: 'ED-R-123 Sperrzone', lat: 52.5300, lng: 13.4200, radius: 2000, type: 'restricted' },
    { id: 2, name: 'ED-D-456 Gefahrenzone', lat: 52.5100, lng: 13.3800, radius: 1500, type: 'danger' },
  ]

  const center: [number, number] = [52.5200, 13.4050]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Taktik-Karte"
        subtitle="Luftraum-Überwachung & taktische Planung"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 h-[700px]">
          <div className="card-header flex items-center justify-between">
            <div className="card-title">Taktik-Karte</div>
            <div className="flex gap-2">
              <select className="input w-40">
                <option value="osm">OpenStreetMap</option>
                <option value="satellite">Satellit</option>
                <option value="hybrid">Hybrid</option>
              </select>
              <button className="btn-secondary btn-sm">Ebene hinzufügen</button>
            </div>
          </div>
          <div className="card-content h-[650px]">
            <MapContainer center={center} zoom={13} className="h-full w-full rounded-lg">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='Tiles &copy; Esri'
                opacity={0.5}
              />

              {/* Airspace Zones */}
              {airspaceZones.map(zone => (
                <Circle
                  key={zone.id}
                  center={[zone.lat, zone.lng]}
                  radius={zone.radius}
                  color={zone.type === 'restricted' ? '#ef4444' : '#f97316'}
                  fillColor={zone.type === 'restricted' ? '#ef4444' : '#f97316'}
                  fillOpacity={0.15}
                  weight={2}
                  dashArray={zone.type === 'restricted' ? '5, 5' : '10, 5'}
                >
                  <Popup>
                    <div className="p-2">
                      <p className="font-bold">{zone.name}</p>
                      <p>Typ: {zone.type === 'restricted' ? 'Sperrzone' : 'Gefahrenzone'}</p>
                      <p>Radius: {zone.radius}m</p>
                    </div>
                  </Popup>
                </Circle>
              ))}

              {/* Drones */}
              {drones.map(drone => (
                <Marker key={drone.id} position={[drone.lat, drone.lng]}>
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm" style={{ transform: `rotate(${drone.heading}deg)` }}>
                      🚁
                    </div>
                    <div className="text-xs text-center bg-gray-800 px-1.5 py-0.5 rounded whitespace-nowrap text-white shadow-lg">
                      {drone.name}
                    </div>
                  </div>
                </Marker>
              ))}

              {/* Mission Area */}
              <Circle
                center={[52.5200, 13.4050]}
                radius={1500}
                color="#C6291A"
                fillColor="#C6291A"
                fillOpacity={0.05}
                weight={2}
                dashArray="10, 10"
              >
                <Popup>
                  <div className="p-2">
                    <p className="font-bold">Einsatzgebiet E-2024-042</p>
                    <p>Radius: 1500m</p>
                    <p>Status: Aktiv</p>
                  </div>
                </Popup>
              </Circle>
            </MapContainer>
          </div>
        </Card>

        <Card className="space-y-4">
          <div className="card-header">
            <div className="card-title">Ebenen & Werkzeuge</div>
          </div>
          <div className="card-content space-y-4">
            <div>
              <h4 className="font-medium mb-2">Kartenebenen</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                  <span>OpenStreetMap</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" />
                  <span>Satellit</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" />
                  <span>Luftraum-Zonen</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                  <span>Drohnen-Positionen</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 text-blue-600 rounded" defaultChecked />
                  <span>Einsatzgebiete</span>
                </label>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Messwerkzeuge</h4>
              <div className="flex gap-2">
                <button className="btn-secondary btn-sm flex-1">Distanz messen</button>
                <button className="btn-secondary btn-sm flex-1">Fläche messen</button>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-2">Aktive Drohnen</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <span>🚁 DJI Matrice 30T</span>
                  <span className="badge-success">Aktiv</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <span>🚁 Mavic 3E</span>
                  <span className="badge-success">Aktiv</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-100 dark:bg-gray-800 rounded">
                  <span>🚁 Mavic 3E</span>
                  <span className="badge-warning">Warnung</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}